import { useState, useEffect } from "react";
import { AxiosError } from "axios";
import { Link } from "react-router-dom";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Table from "../../../core/common/dataTable/index";
import { LevelTable } from "../../../core/data/interface"; //Les types des colones de notre tableau déclaré au préalable
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { toast } from "sonner";
// pour restreaindre l'accès à ce composant
import withPermission from "../../../core/common/autorisations/withPermission";
import { useAcademicYear } from "../../../core/common/context/AcademicYearContext";

import {
  Action,
  buildPermission,
  buildPermissionContext,
  Resource,
} from "../../../core/common/autorisations/permissionsMap";
import InfosAlertYearDisabled from "../../../core/common/alerts/infosAlertYearDisabled";
import FinanceMenu from "../financeMenu";
import CreatePaymentPlanModal from "./createPaymentPlanModal";
import EditPaymentPlanModal from "./editPaymentPlanModal";
import AssignPayersModal from "./assignPayersModal";
import {
  deletePaymentPlan,
  getAllPaymentPlans,
  getStudentsByPaymentPlan,
} from "../../../api/paiementPlans";
import { getPaymentSettingsByYear } from "../../../api/paiement_settings";

// boutton protégée pour créer un plan de paiement
const ProtectedBtnAddPaymentPlan = withPermission(
  (props: any) => (
    <div className="mb-2">
      <button
        onClick={() => {
          props.setShowCreate(true);
        }}
        //   to="#"
        className="btn btn-primary d-flex align-items-center"
      >
        <i className="ti ti-square-rounded-plus me-2" />
        Nouveau Plan de Paiement
      </button>
    </div>
  ),
  [buildPermission(Resource.PAYMENT_PLAN, Action.CREATE)],
  "masquer",
);

// boutton protégée pour modifier un plan de paiement
const ProtectedBtnUpdatePaymentPlan = withPermission(
  (props: any) => (
    <li>
      <Link
        className="dropdown-item rounded-1"
        to="#"
        onClick={() => {
          props.setEditPlan(props.record);
          props.setShowEdit(true);
        }}
      >
        <i className="ti ti-edit-circle me-2" />
        Modifier
      </Link>
    </li>
  ),
  [buildPermission(Resource.PAYMENT_PLAN, Action.UPDATE)],
  "masquer",
);

// boutton protégée pour supprimer un plan de paiement
const ProtectedBtnDeletePaymentPlan = withPermission(
  (props: any) => (
    <li>
      <Link
        onClick={() => props.showConfirmationAlert(props.record)}
        className="dropdown-item rounded-1"
        to="#"
      >
        <i className="ti ti-trash-x me-2" />
        Supprimer
      </Link>
    </li>
  ),
  [buildPermission(Resource.PAYMENT_PLAN, Action.DELETE)],
  "masquer",
);

// boutton protégée pour gérer les payeurs d'un plan de paiement
const ProtectedBtnAffectedPayer = withPermission(
  (props: any) => (
    <li>
      <Link
        onClick={() => {
          props.setAssignPlan(props.record);
          props.setShowAssign(true);
        }}
        className="dropdown-item rounded-1"
        to="#"
      >
        <i className="ti ti-users me-2" />
        Payeurs
      </Link>
    </li>
  ),
  [
    ...Object.values(Action).map((action) =>
      buildPermission(Resource.STUDENT_PAYMENT_PLAN, action),
    ),
  ],
  "masquer",
);

const PaymentPlan = () => {
  const { selectedYear, setSelectedYear } = useAcademicYear();

  //Initialisation des state utilisés
  const [allPaymentPlans, setAllPaymentPlans] = useState<any[]>([]);
  const [editPlan, setEditPlan] = useState<any>();
  const [isLoading, setIsLoading] = useState(true); // Pour le vrai chargement
  const [hasLoaded, setHasLoaded] = useState(false); // Pour éviter "Chargement..." quand il n'y a juste pas de données

  const [refreshKey, setRefreshKey] = useState(0);

  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const [assignPlan, setAssignPlan] = useState<any>();

  const [paymentSettings, setPaymentSettings] = useState<any>();

  //Initialisation de sweetAlert
  const MySwal = withReactContent(Swal);

  // Les colonnes de notre tableau
  const columns = [
    {
      title: "Libellé",
      dataIndex: "label",
      sorter: (a: any, b: any) => a?.label?.length - b?.label?.length,
    },
    {
      title: `Montant ${paymentSettings?.currency}`,
      dataIndex: "amount",
      render: (amount: number) => (
        <span>{Number(amount).toLocaleString("fr-FR")}</span>
      ),
      sorter: (a: any, b: any) => a?.amount - b?.amount,
    },
    {
      title: "Nombre de Payeurs",
      dataIndex: "affectedStudents",
      render: (numberOfStudents: any) => {
        // console.log(`numberOfStudents: ${numberOfStudents}`)
        return <span>{numberOfStudents?.length} Etudiant(s)</span>;
      },
      sorter: (a: any, b: any) => a?.numberOfStudents - b?.numberOfStudents,
    },
    {
      title: "Echéance",
      dataIndex: "due_date",
      render: (date: string) => {
        const d = new Date(date);
        return d.toLocaleDateString("fr-FR", {
          year: "numeric",
          month: "long",
        });
      },
      sorter: (a: any, b: any) =>
        new Date(a?.due_date).getTime() - new Date(b?.due_date).getTime(),
    },
    {
      title: "Obligatoire ?",
      dataIndex: "to_ignore",
      render: (to_ignore: boolean) => (
        <span>{! to_ignore ? "Oui" : "Non"}</span>
      ),
      filters: [
        { text: "Oui", value: true },
        { text: "Non", value: false },
      ],
      onFilter: (value: any, record: any) => record.to_ignore === value,
    },
    {
      title: "Date de création",
      dataIndex: "createdAt",
      render: (date: string) => {
        const d = new Date(date);
        return d.toLocaleDateString("fr-FR", {
          year: "numeric",
          month: "long",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      },
      sorter: (a: LevelTable, b: LevelTable) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (_: any, record: any) => (
        <>
          <div className="d-flex align-items-center">
            <div className="dropdown">
              <Link
                to="#"
                className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <i className="ti ti-dots-vertical fs-14" />
              </Link>
              <ul className="dropdown-menu dropdown-menu-right p-3">
                {/* Affecter des payeurs à ce plan */}
                <ProtectedBtnAffectedPayer
                  record={record}
                  setShowAssign={setShowAssign}
                  setAssignPlan={setAssignPlan}
                />

                {/* modifier un niveau */}
                <ProtectedBtnUpdatePaymentPlan
                  setEditPlan={setEditPlan}
                  record={record}
                  setShowEdit={setShowEdit}
                />

                {/* supprimer un niveau */}
                <ProtectedBtnDeletePaymentPlan
                  showConfirmationAlert={showConfirmationAlert}
                  record={record}
                />
              </ul>
            </div>
          </div>
        </>
      ),
    },
  ];

  //Traitement en alert de la demande de réinitialisation de mot de passe
  const showConfirmationAlert = (plan: any) => {
    MySwal.fire({
      title: "Supprimer un Plan de Paiement ?",
      text: `Souhaitez-vous vraiment supprimer le plan de paiement ${plan.label} ?`,
      showCancelButton: true,
      // confirmButtonColor: "#00ff00",
      confirmButtonText: "Envoyer!",
      // cancelButtonColor: "#ff0000",
      cancelButtonText: "Annuler",
    }).then(async (result) => {
      if (result.isConfirmed) {
        // ── Affiche le spinner pendant la requête ──
        MySwal.fire({
          title: "Suppression en cours...",
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          didOpen: () => MySwal.showLoading(),
        });
        try {
          //On lance la requête d'envoi de mail
          await deletePaymentPlan(plan.id);
          MySwal.fire({
            title: "Plan de paiement supprimé avec succès !",
            icon: "success",
            confirmButtonText: "OK",
          });

          reloadData(); //On reload la liste
        } catch (error) {
          const err = error as AxiosError<any>;
          //On affiche une alerte en cas d'erreur côté serveur
          MySwal.fire({
            title: "Erreur",
            text:
              err.response?.data?.message ||
              err.response?.data?.error ||
              "Une erreur est survenue, réessayez plus tard",
            icon: "error",
            confirmButtonText: "OK",
          });
        }
      }
    });
  };

  //Reload de la liste
  const reloadData = () => setRefreshKey((prev) => prev + 1);

  // fonction pour récupérer tous les plans de payment
  const fetchData = async () => {
    // On attend que selectedYear soit défini
    if (!selectedYear?.id) return;

    setHasLoaded(false);
    setIsLoading(true);

    try {
      const resp = await getAllPaymentPlans(selectedYear?.id);
      const paymentPlans = resp.data;

      const formattedPaymentPlans: any[] =
        (await Promise.all(
          paymentPlans.map(async (plan: any) => {
            const affectedStudents = await getStudentsByPaymentPlan(
              plan?.id,
              buildPermissionContext(Resource.PAYMENT_PLAN, Action.READ),
            );
            return {
              ...plan,
              affectedStudents: affectedStudents.data,
            };
          }),
        )) ?? [];

      // console.log('All payment plans: ', formattedPaymentPlans)
      setAllPaymentPlans(formattedPaymentPlans);
    } catch (error: any) {
      // console.log("Error fetching payment plans: ", error);
      // Si erreur (Toast Error)
      toast.error(
        error.message || "Une erreur s'est produit, Réessayer plus tard",
      );
    } finally {
      setIsLoading(false); //Les données ne sont plus en chargement
      setHasLoaded(true); //Les données ont été chargé avec succès
    }
  };

  // fonction pour récupérer les paramêtres de paiement de l'année academique
  const fetchPaymentParameters = async () => {
    try {
      if (!selectedYear) return;
      const res = await getPaymentSettingsByYear(
        selectedYear?.id,
        buildPermissionContext(Resource.PAYMENT_PLAN, Action.READ),
      );
      // console.log("Parameters: ", res.data);
      setPaymentSettings(res?.data[0]);
    } catch (error: any) {
      // console.log("Error fetching payment parameters:", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  useEffect(() => {
    fetchPaymentParameters();
  }, [selectedYear]);

  //On recupère la liste des plan de paiement en appelant sa fonction
  useEffect(() => {
    fetchData();
  }, [refreshKey, selectedYear]);

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <FinanceMenu />
            {/* Boutton en entête de la page */}
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              {/* <TooltipOption/> */}
              <ProtectedBtnAddPaymentPlan setShowCreate={setShowCreate} />
            </div>
          </div>
          {/* /Page Header */}
          {!selectedYear?.statut && <InfosAlertYearDisabled />}
          {/* Tableau liste du personnel administratif */}
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-3">Plans de Paiements</h4>
            </div>
            <div className="card-body p-0 py-3">
              {/* Staffs List (On affiche le tableau qui si les données son chargées même vide) */}
              {isLoading && !hasLoaded ? (
                <div className="text-center">
                  <div className="spinner-border" role="status"></div>
                  <span className="visually">Chargement des Données...</span>
                </div>
              ) : (
                <Table
                  columns={columns}
                  dataSource={allPaymentPlans}
                  Selection={true}
                />
              )}
              {/* /Staffs List */}
            </div>
          </div>
        </div>
        {/* /Page Header */}

        <EditPaymentPlanModal
          show={showEdit}
          plan={editPlan}
          onClose={() => setShowEdit(false)}
          onSaved={reloadData}
        />
        <CreatePaymentPlanModal
          show={showCreate}
          onClose={() => setShowCreate(false)}
          onSaved={reloadData}
        />
        <AssignPayersModal
          show={showAssign}
          paymentPlan={assignPlan}
          onClose={() => setShowAssign(false)}
          onSaved={reloadData}
        />
      </div>
    </>
  );
};

// export default withPermission(AcademicYearList, "admin:access", "accès-refuser");
export default PaymentPlan;
